import { useParams } from 'react-router-dom';
import { Spin, Button } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useOrderDetail } from 'commons/hooks/use-order-detail';
import styles from './index.module.scss';
import { STOCK_PLACE_MAP } from 'constant/stock-place';

const INVOICE_SEQ_STORAGE_KEY = 'sg_invoice_seq_v2';
const INVOICE_PREVIEW_STORAGE_KEY_PREFIX = 'sg_invoice_preview_v2';

type InvoicePreview = {
  invoiceNo: string;
  seq: number;
  date: string;
};

const getToday = () => dayjs().format('YYYYMMDD');

const createInvoicePreview = (
  orderDate: string,
  seq: number,
  printDate: string,
): InvoicePreview => ({
  date: printDate,
  seq,
  invoiceNo: `${orderDate}${String(seq).padStart(3, '0')}`,
});

const getInvoicePreviewStorageKey = (orderId?: string) => {
  if (!orderId) return null;
  return `${INVOICE_PREVIEW_STORAGE_KEY_PREFIX}_${orderId}`;
};

const getInvoicePreview = (orderDate: string): InvoicePreview => {
  const today = getToday();
  if (typeof window === 'undefined') {
    return createInvoicePreview(orderDate, 1, today);
  }

  let lastSeq = 0;
  try {
    const raw = window.localStorage.getItem(INVOICE_SEQ_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as {
        date?: string;
        seq?: number;
        lastSeq?: number;
      };
      if (parsed?.date === today) {
        if (Number.isFinite(parsed.lastSeq)) {
          lastSeq = Number(parsed.lastSeq);
        } else if (Number.isFinite(parsed.seq)) {
          lastSeq = Number(parsed.seq);
        }
      }
    }
  } catch (error) {
    lastSeq = 0;
  }

  return createInvoicePreview(orderDate, lastSeq + 1, today);
};

const commitInvoiceSeq = (date: string, seq: number) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      INVOICE_SEQ_STORAGE_KEY,
      JSON.stringify({ date, lastSeq: seq }),
    );
  } catch (error) {
    // ignore storage errors
  }
};

const getStoredInvoicePreview = (orderId?: string): InvoicePreview | null => {
  if (typeof window === 'undefined') return null;
  const storageKey = getInvoicePreviewStorageKey(orderId);
  if (!storageKey) return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<InvoicePreview>;
    if (!Number.isFinite(parsed.seq) || typeof parsed.date !== 'string') {
      return null;
    }
    // date 存的是打印日期，非当天则缓存失效
    if (parsed.date !== getToday()) return null;
    return parsed as InvoicePreview;
  } catch (error) {
    return null;
  }
};

const persistInvoicePreview = (
  orderId: string | undefined,
  preview: InvoicePreview,
) => {
  if (typeof window === 'undefined') return;
  const storageKey = getInvoicePreviewStorageKey(orderId);
  if (!storageKey) return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(preview));
  } catch (error) {
    // ignore storage errors
  }
};

const getInitialInvoicePreview = (
  orderId: string | undefined,
  orderDate: string,
): InvoicePreview => {
  const storedPreview = getStoredInvoicePreview(orderId);
  if (storedPreview) return storedPreview;
  return getInvoicePreview(orderDate);
};

const getOrderDate = (order: {
  receiveTime?: string;
  deliveryTime?: string;
  paymentTime?: string;
  createTime: string;
}) =>
  dayjs(
    order.receiveTime ||
      order.deliveryTime ||
      order.paymentTime ||
      order.createTime,
  ).format('YYYYMMDD');

const SGOrderInvoice = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [invoicePreview, setInvoicePreview] = useState<InvoicePreview | null>(
    null,
  );

  const { omsOrderDetail, productList } = useOrderDetail(
    orderId ? Number(orderId) : undefined,
  );

  const orderDate = useMemo(
    () => (omsOrderDetail ? getOrderDate(omsOrderDetail) : null),
    [omsOrderDetail],
  );

  useEffect(() => {
    if (!orderDate) return;
    const preview = getInitialInvoicePreview(orderId, orderDate);
    setInvoicePreview((prev) => {
      if (prev && prev.date === preview.date && prev.seq === preview.seq) {
        return prev;
      }
      return preview;
    });
  }, [orderId, orderDate]);

  if (!omsOrderDetail || !productList.length || !invoicePreview)
    return <Spin />;

  // 格式化日期
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('D/M/YYYY');
  };

  // 格式化金额
  const formatAmount = (amount: number) => {
    return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // 计算GST（使用商品库存地判断）
  const stockPlace = productList[0]?.stockPlace;
  const isSingaporeAsuGst = stockPlace === STOCK_PLACE_MAP.SINGAPORE_ASU_GST;
  const isSingapore = stockPlace === STOCK_PLACE_MAP.SINGAPORE_ASU;
  const showGstRow = !isSingapore;
  const gstLabel = isSingaporeAsuGst ? 'Total GST 9% :' : 'Add 0% GST :';
  const invoiceTitle = isSingapore ? 'SALES INVOICE' : 'Tax Invoice';
  const invoiceNoLabel = isSingapore ? 'Invoice No :' : 'Tax Invoice No :';
  const subTotal = omsOrderDetail.payAmountActualCurrency || 0;
  const actualCurrencyRate =
    omsOrderDetail.orderItemList[0]?.actualCurrencyRate ?? 1;
  const integrationDeduction = omsOrderDetail.useIntegration
    ? Math.round(omsOrderDetail.useIntegration * actualCurrencyRate)
    : 0;
  const gstAmount = omsOrderDetail.totalTaxAmount ?? 0;

  // 处理打印
  const handlePrint = () => {
    commitInvoiceSeq(invoicePreview.date, invoicePreview.seq);
    persistInvoicePreview(orderId, invoicePreview);
    window.print();
  };

  return (
    <div>
      <div className={styles.invoice}>
        {/* 头部信息 */}
        <div className={styles.header}>
          <div className={styles.companyName}>ASU COMPANY PTE. LTD.</div>
          <div className={styles.companyReg}>Co Reg. No. 201939838Z</div>
          <div className={styles.address}>
            391B Orchard Road #23-05 Ngee Ann City Singapore 238874
          </div>
          <div className={styles.contact}>
            Tel: +65 6530 3529 Fax: +65 65303528
          </div>
          <div className={styles.title}>{invoiceTitle}</div>
        </div>

        {/* 发票信息 */}
        <div className={styles.invoiceInfo}>
          <div className={styles.toSection}>
            <div className={styles.toLabel}>TO :</div>
            <div className={styles.toContent}>
              {omsOrderDetail.receiverName}
              <br />
              {omsOrderDetail.receiverDetailAddress}
              <br />
              {omsOrderDetail.receiverCity} {omsOrderDetail.receiverProvince}
              <br />
              {omsOrderDetail.receiverPhone}
            </div>
          </div>
          <div className={styles.infoSection}>
            <div className={styles.infoRow}>
              GST Registration No. 201939838Z
            </div>
            <div className={styles.infoRow}>
              Date:{' '}
              {formatDate(
                omsOrderDetail.receiveTime ||
                  omsOrderDetail.deliveryTime ||
                  omsOrderDetail.paymentTime ||
                  omsOrderDetail.createTime,
              )}
            </div>
            <div className={styles.infoRow}>
              {invoiceNoLabel} {invoicePreview.invoiceNo}
            </div>
            <div className={styles.infoRow}>Terms : C.O.D</div>
          </div>
        </div>

        {/* 商品列表 */}
        <table className={styles.itemsTable}>
          <thead>
            <tr>
              <th className={styles.itemNo}>NO</th>
              <th className={styles.itemDescription}>Description</th>
              <th className={styles.itemPrice}>Unit price (S$)</th>
            </tr>
          </thead>
          <tbody>
            {omsOrderDetail.orderItemList.map((item, index) => (
              <tr key={item.id} className={styles.itemRow}>
                <td className={styles.itemNo}>{index + 1}</td>
                <td className={styles.itemDescription}>
                  {item.productId}
                  <br />
                  {item.productName}
                </td>
                <td className={styles.itemPrice}>
                  {formatAmount(item.productPriceActualCurrency ?? 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 总计 */}
        <div className={styles.summary}>
          {!!omsOrderDetail.freightAmountActualCurrency && (
            <div className={styles.summaryRow}>
              <span className={styles.label}>Freight :</span>
              <span className={styles.value}>
                {formatAmount(omsOrderDetail.freightAmountActualCurrency)}
              </span>
            </div>
          )}
          {!!omsOrderDetail.promotionAmountActualCurrency && (
            <div className={styles.summaryRow}>
              <span className={styles.label}>Discount :</span>
              <span className={styles.value}>
                -{formatAmount(omsOrderDetail.promotionAmountActualCurrency)}
              </span>
            </div>
          )}
          {!!integrationDeduction && (
            <div className={styles.summaryRow}>
              <span className={styles.label}>Points Deduction :</span>
              <span className={styles.value}>
                -{formatAmount(integrationDeduction)}
              </span>
            </div>
          )}

          <div className={styles.summaryRow}>
            <span className={styles.label}>
              {isSingapore
                ? 'Sub Total Amount :'
                : 'Total amount payable including GST :'}
            </span>
            <span className={styles.value}>{formatAmount(subTotal)}</span>
          </div>

          {showGstRow && (
            <div className={styles.summaryRow}>
              <span className={styles.label}>Total amount excluding GST :</span>
              <span className={styles.value}>
                {formatAmount(subTotal - gstAmount)}
              </span>
            </div>
          )}

          {showGstRow && (
            <div className={styles.summaryRow}>
              <span className={styles.label}>{gstLabel}</span>
              <span className={styles.value}>{formatAmount(gstAmount)}</span>
            </div>
          )}
        </div>

        {/* 免责声明 */}
        <div className={styles.disclaimer}>
          ITEMS SOLD CANNOT BE RETURNED OR EXCHANGED
        </div>

        {/* 付款信息 */}
        <div className={styles.paymentInfo}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              {isSingapore ? (
                <>
                  The statement 'Goods are sold under GST Gross Margin Scheme.
                  <br />
                  Both the seller and buyer cannot claim any input tax on the
                  goods'.
                </>
              ) : (
                'Cheque payment to "ASU Company Pte. Ltd."'
              )}
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Bank Transfer to</div>
            <div className={styles.sectionContent}>
              Beneficiary: ASU Company Pte. Ltd.
              <br />
              Account No: 601-798325-001
              <br />
              Bank: OCBC Bank
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              For payment via Corporate Paynow
            </div>
            <div className={styles.sectionContent}>
              - Enter PayNow ID 201939838ZOCB into the Unique Entity Number
              (UEN) field
            </div>
          </div>
        </div>
      </div>

      {/* 打印按钮 - 只在屏幕显示时显示 */}
      <div className={styles.printButton}>
        <Button type="primary" onClick={handlePrint}>
          打印发票
        </Button>
      </div>
    </div>
  );
};

export default SGOrderInvoice;
